import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, Loader2, Sparkles, Zap, ArrowLeft, Clock, Search, Bot, User as UserIcon, AlertTriangle, Star, Download, FileText, MessagesSquare } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import usePageSeo from "@/hooks/usePageSeo";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return `Today ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
    const diffDays = Math.round((now - d) / (1000 * 60 * 60 * 24));
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: d.getFullYear() === now.getFullYear() ? undefined : "numeric" });
  } catch { return iso; }
};

// Humanize the agent tool slug for a chip label ("get_my_holdings" → "get my holdings").
const prettyTool = (slug) => (slug || "").replace(/_/g, " ");

// Build a human-readable Markdown document from a transcript. Used for both
// the raw .md download AND as source material for the html2pdf export.
function buildMarkdown(session) {
  const header = [
    `# Ask DPP conversation`,
    ``,
    `**Session:** \`${session.session_id}\``,
    `**Started:** ${session.created_at || "-"}`,
    `**Last updated:** ${session.updated_at || "-"}`,
    `**Turns:** ${session.turn_count}`,
    ``,
    `---`,
    ``,
  ];
  const body = [];
  for (const m of (session.messages || [])) {
    const who = m.role === "user" ? "**You**" : "**Ask DPP (Agent)**";
    body.push(`### ${who} · _${m.at || ""}_`);
    body.push("");
    body.push(m.content || "");
    if (Array.isArray(m.tools_used) && m.tools_used.length) {
      body.push("");
      body.push(
        "> _Tools called: " +
          m.tools_used
            .map((t) => `\`${t.name}\`${t.args && Object.keys(t.args).length ? "(" + JSON.stringify(t.args) + ")" : ""}`)
            .join(", ") +
          "_"
      );
    }
    body.push("");
    body.push("---");
    body.push("");
  }
  return header.join("\n") + body.join("\n");
}

// Trigger a browser download for a Blob with the given filename.
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function exportAsMarkdown(session) {
  const md = buildMarkdown(session);
  const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
  const stamp = (session.updated_at || new Date().toISOString()).slice(0, 10);
  downloadBlob(blob, `ask-dpp-${stamp}-${session.session_id.slice(0, 8)}.md`);
}

async function exportAsPdf(session) {
  // Render the Markdown-ish content into a hidden printable div, then let
  // html2pdf.js rasterize it. We keep the HTML intentionally simple so the
  // PDF stays readable without a full markdown-to-HTML renderer.
  const html2pdf = (await import("html2pdf.js")).default;
  const container = document.createElement("div");
  container.style.cssText = "position:fixed;left:-9999px;top:0;width:720px;padding:32px;font-family:Georgia, 'Times New Roman', serif;color:#0F2A44;background:#ffffff;line-height:1.5;";
  const esc = (s) => (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br>");
  const rows = (session.messages || []).map((m) => {
    const who = m.role === "user" ? "You" : "Ask DPP · Agent";
    const bubbleBg = m.role === "user" ? "#0F2A44" : "#F6F8FB";
    const bubbleColor = m.role === "user" ? "#ffffff" : "#0F2A44";
    const align = m.role === "user" ? "right" : "left";
    const toolsHtml = (Array.isArray(m.tools_used) && m.tools_used.length)
      ? `<div style="margin-top:4px;font-size:10px;color:#71809A;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;text-transform:uppercase;letter-spacing:0.1em;">Tools: ${m.tools_used.map((t) => esc(t.name)).join(" · ")}</div>`
      : "";
    return `<div style="margin:12px 0;text-align:${align};">
      <div style="display:inline-block;max-width:85%;text-align:left;padding:10px 14px;border-radius:12px;background:${bubbleBg};color:${bubbleColor};font-family:ui-sans-serif,-apple-system,sans-serif;font-size:13px;">
        <div style="font-size:10px;opacity:0.65;margin-bottom:4px;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;text-transform:uppercase;letter-spacing:0.1em;">${who}</div>
        <div>${esc(m.content)}</div>
      </div>
      ${toolsHtml}
    </div>`;
  }).join("");
  container.innerHTML = `
    <div style="border-bottom:2px solid #F59E0B;padding-bottom:12px;margin-bottom:20px;">
      <div style="font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:10px;text-transform:uppercase;letter-spacing:0.25em;color:#F59E0B;">Ask DPP · Your Personal AI Agent</div>
      <h1 style="margin:4px 0 0 0;font-size:28px;">Conversation transcript</h1>
      <div style="margin-top:6px;color:#71809A;font-size:12px;">
        Session <code>${esc(session.session_id)}</code> · ${esc(session.updated_at || "")} · ${session.turn_count} turns
      </div>
    </div>
    ${rows}
    <div style="margin-top:32px;padding-top:12px;border-top:1px solid #E5E9F0;font-size:10px;color:#71809A;text-align:center;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;text-transform:uppercase;letter-spacing:0.15em;">
      Generated by dividendportfolioplanner.com — Not investment advice
    </div>
  `;
  document.body.appendChild(container);
  const stamp = (session.updated_at || new Date().toISOString()).slice(0, 10);
  try {
    await html2pdf()
      .set({
        margin: 10,
        filename: `ask-dpp-${stamp}-${session.session_id.slice(0, 8)}.pdf`,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      })
      .from(container)
      .save();
  } finally {
    container.remove();
  }
}

/**
 * My Conversations — browse past Ask DPP sessions, see which tools the agent
 * invoked for each turn, and open any thread to review the full transcript.
 * The chat widget stays the primary input surface — this page is a read-only
 * history view, since re-sending a message to an old session_id would spin up
 * the LLM again with a stale context.
 */
export default function Conversations() {
  usePageSeo({
    title: "My conversations · Ask DPP",
    description: "Browse your past Ask DPP agent conversations — tool invocations, portfolio lookups, backtests, and ticker research.",
    canonical: "/conversations",
    noindex: true,
  });
  const { user } = useAuth();
  const nav = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);  // selected session_id
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get("/chatbot/my-conversations");
      setSessions(r.data.sessions || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Sign in to view your conversations");
        nav("/auth");
      } else {
        toast.error("Could not load your conversations");
      }
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => { if (user) load(); }, [user, load]);

  // Filter sessions by the free-text search box. We match against the first
  // question, last reply, and every tool name — so "backtest" surfaces every
  // thread where the user ran one, even if the question didn't use that word.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sessions;
    return sessions.filter((s) => {
      const hay = [
        s.first_question,
        s.last_reply,
        ...(s.tools_used || []).map((t) => t.name),
      ].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [sessions, query]);

  const togglePin = useCallback(async (sessionId, currentlyPinned) => {
    // Optimistic update so the card re-sorts instantly.
    setSessions((prev) => {
      const next = prev.map((s) =>
        s.session_id === sessionId ? { ...s, pinned: !currentlyPinned, pinned_at: !currentlyPinned ? new Date().toISOString() : null } : s
      );
      // Re-sort: pinned first, then by pinned_at desc, then updated_at desc.
      return [...next].sort((a, b) => {
        if (!!a.pinned !== !!b.pinned) return a.pinned ? -1 : 1;
        if (a.pinned && b.pinned) return (b.pinned_at || "").localeCompare(a.pinned_at || "");
        return (b.updated_at || "").localeCompare(a.updated_at || "");
      });
    });
    try {
      await api.post(`/chatbot/sessions/${sessionId}/pin`, { pinned: !currentlyPinned });
      toast.success(!currentlyPinned ? "Pinned to top" : "Unpinned");
    } catch (e) {
      // Rollback on failure.
      toast.error("Could not update pin");
      load();
    }
  }, [load]);

  const resumeChat = useCallback((session) => {
    // Dispatch an app-level event the ChatbotWidget listens for. The widget
    // reuses `session.session_id` on its next POST so the backend keeps
    // appending to the SAME chatbot_sessions doc rather than forking a new one.
    const detail = {
      session_id: session.session_id,
      messages: (session.messages || []).map((m) => ({
        role: m.role === "assistant" ? "bot" : m.role,
        content: m.content,
        tools_used: m.tools_used || [],
      })),
    };
    window.dispatchEvent(new CustomEvent("dpp:open-chatbot", { detail }));
    toast.success("Picking up where you left off…");
  }, []);

  const current = useMemo(
    () => sessions.find((s) => s.session_id === selected) || null,
    [sessions, selected]
  );

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <MessageCircle className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
        <h1 className="serif text-3xl mb-2">Sign in to view your conversations</h1>
        <Button onClick={() => nav("/auth")} data-testid="conversations-signin-cta">Sign in</Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-[0.25em] text-primary">
            <Sparkles className="w-3.5 h-3.5" /> Ask DPP · Your Personal AI Agent
          </div>
          <h1 className="serif text-3xl sm:text-4xl mt-1">My conversations</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Every chat with Ask DPP is logged here with the agent's tool calls — portfolio lookups, backtests, dividend reads, live quotes. Open any thread to review what the agent fetched for you.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={load}
          disabled={loading}
          data-testid="conversations-refresh"
          className="rounded-full"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-24 rounded-md bg-muted animate-pulse" />)}
        </div>
      ) : sessions.length === 0 ? (
        <EmptyState onStart={() => nav("/dashboard")} />
      ) : selected && current ? (
        <TranscriptView
          session={current}
          onBack={() => setSelected(null)}
          onResume={() => resumeChat(current)}
          onTogglePin={() => togglePin(current.session_id, current.pinned)}
        />
      ) : (
        <>
          <div className="mb-4 relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search your conversations…"
              className="pl-9"
              data-testid="conversations-search"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="conversations-list">
            {filtered.map((s) => (
              <SessionCard
                key={s.session_id}
                session={s}
                onOpen={() => setSelected(s.session_id)}
                onResume={() => resumeChat(s)}
                onTogglePin={() => togglePin(s.session_id, s.pinned)}
              />
            ))}
            {filtered.length === 0 && (
              <div className="col-span-full text-center text-sm text-muted-foreground py-10">
                No conversations match "{query}".
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SessionCard({ session, onOpen, onResume, onTogglePin }) {
  const tools = session.tools_used || [];
  const uniqueTools = [...new Set(tools.map((t) => t.name))].slice(0, 4);
  return (
    <Card
      className={`hover:border-primary/40 transition-colors cursor-pointer group ${
        session.pinned ? "border-secondary/60 bg-secondary/[0.04]" : ""
      }`}
      onClick={onOpen}
      data-testid={`conversation-card-${session.session_id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mono text-[9px] uppercase tracking-widest text-muted-foreground">
              <Clock className="w-3 h-3" />
              {fmtDate(session.updated_at || session.created_at)}
              <span>·</span>
              <span>{session.turn_count} turn{session.turn_count === 1 ? "" : "s"}</span>
              {session.pinned && (
                <span className="inline-flex items-center gap-1 text-secondary ml-1">
                  <Star className="w-3 h-3 fill-current" /> pinned
                </span>
              )}
              {session.escalated && (
                <span className="inline-flex items-center gap-1 text-amber-600 ml-1">
                  <AlertTriangle className="w-3 h-3" /> escalated
                </span>
              )}
            </div>
            <div className="serif text-base mt-1.5 line-clamp-2 group-hover:text-primary transition-colors">
              {session.first_question || "(empty conversation)"}
            </div>
            {session.last_reply && (
              <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                {session.last_reply}
              </div>
            )}
          </div>
          {/* Pin toggle — stopPropagation so clicking the star doesn't also
              open the transcript. */}
          <button
            onClick={(e) => { e.stopPropagation(); onTogglePin?.(); }}
            className={`shrink-0 w-7 h-7 rounded-full inline-flex items-center justify-center transition-colors ${
              session.pinned
                ? "text-secondary bg-secondary/15 hover:bg-secondary/25"
                : "text-muted-foreground hover:text-secondary hover:bg-muted"
            }`}
            data-testid={`conversation-pin-${session.session_id}`}
            aria-label={session.pinned ? "Unpin conversation" : "Pin conversation"}
            title={session.pinned ? "Unpin from top" : "Pin to top"}
          >
            <Star className={`w-4 h-4 ${session.pinned ? "fill-current" : ""}`} />
          </button>
        </div>
        <div className="flex items-center justify-between mt-3 gap-3">
          {uniqueTools.length > 0 ? (
            <div className="flex flex-wrap gap-1 flex-1 min-w-0">
              {uniqueTools.map((t, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 mono text-[9px] uppercase tracking-widest rounded-full bg-primary/10 text-primary px-2 py-0.5"
                >
                  <Zap className="w-2.5 h-2.5" />
                  {prettyTool(t)}
                </span>
              ))}
            </div>
          ) : <div className="flex-1" />}
          <button
            onClick={(e) => { e.stopPropagation(); onResume?.(); }}
            className="shrink-0 inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 mono uppercase tracking-widest"
            data-testid={`conversation-resume-${session.session_id}`}
            title="Pick up where you left off"
          >
            <MessagesSquare className="w-3 h-3" />
            Continue
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

function TranscriptView({ session, onBack, onResume, onTogglePin }) {
  const nav = useNavigate();
  const [exporting, setExporting] = useState(null); // null | "md" | "pdf"
  const messages = session.messages || [];

  const doExport = async (kind) => {
    setExporting(kind);
    try {
      if (kind === "md") await exportAsMarkdown(session);
      else await exportAsPdf(session);
      toast.success(kind === "md" ? "Markdown saved" : "PDF saved");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("export failed", e);
      toast.error("Export failed");
    } finally {
      setExporting(null);
    }
  };

  return (
    <div data-testid="conversation-transcript">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <Button variant="ghost" onClick={onBack} data-testid="conversation-back" className="rounded-full">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> All conversations
        </Button>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            className="rounded-full"
            onClick={onTogglePin}
            data-testid="transcript-pin"
          >
            <Star className={`w-3.5 h-3.5 mr-1.5 ${session.pinned ? "fill-secondary text-secondary" : ""}`} />
            {session.pinned ? "Unpin" : "Pin to top"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="rounded-full"
            onClick={() => doExport("md")}
            disabled={exporting !== null}
            data-testid="transcript-export-md"
            title="Download as Markdown — great for pasting into notes apps."
          >
            {exporting === "md" ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1.5" />}
            Markdown
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="rounded-full"
            onClick={() => doExport("pdf")}
            disabled={exporting !== null}
            data-testid="transcript-export-pdf"
            title="Download a formatted PDF — great for sharing a backtest comparison."
          >
            {exporting === "pdf" ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
            PDF
          </Button>
          <Button
            size="sm"
            className="rounded-full"
            onClick={onResume}
            data-testid="transcript-resume"
          >
            <MessagesSquare className="w-3.5 h-3.5 mr-1.5" />
            Continue this chat
          </Button>
        </div>
      </div>
      <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
        Session {session.session_id.slice(0, 8)} · {fmtDate(session.updated_at)}
      </div>
      <Card>
        <CardContent className="p-4 sm:p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
              <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
                m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary/15 text-secondary"
              }`}>
                {m.role === "user" ? <UserIcon className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
              </div>
              <div className={`max-w-[80%] ${m.role === "user" ? "text-right" : ""}`}>
                <div
                  className={`rounded-lg px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-primary text-primary-foreground inline-block text-left"
                      : "bg-muted text-foreground border border-border/60"
                  }`}
                >
                  {m.content}
                </div>
                {Array.isArray(m.tools_used) && m.tools_used.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1" data-testid={`transcript-tools-${i}`}>
                    {m.tools_used.map((t, j) => (
                      <span
                        key={j}
                        className={`inline-flex items-center gap-1 mono text-[9px] uppercase tracking-widest rounded-full px-2 py-0.5 ${
                          t.ok === false ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary"
                        }`}
                        title={t.args && Object.keys(t.args).length ? `args: ${JSON.stringify(t.args)}` : ""}
                      >
                        <Zap className="w-2.5 h-2.5" />
                        {prettyTool(t.name)}
                      </span>
                    ))}
                  </div>
                )}
                <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground mt-1">
                  {fmtDate(m.at)}
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
      <div className="mt-4 text-xs text-muted-foreground">
        Tap <b>Continue this chat</b> to open Ask DPP with this exact session, or <button className="underline hover:text-primary" onClick={() => nav("/dashboard")} data-testid="conversation-open-chatbot">open the dashboard</button> to start a fresh thread.
      </div>
    </div>
  );
}

function EmptyState({ onStart }) {
  return (
    <Card>
      <CardContent className="p-10 text-center">
        <MessageCircle className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
        <h2 className="serif text-xl mb-2">No conversations yet</h2>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          Open <b>Ask DPP</b> from the floating chat button on any page and ask things like "what's my portfolio worth?" or "backtest my dividend starter."
        </p>
        <Button className="mt-4 rounded-full" onClick={onStart} data-testid="conversations-cta-start">
          Open the dashboard
        </Button>
      </CardContent>
    </Card>
  );
}
